// Checkout or the billing portal — one question, asked from two places
// (issue #501): the 402 path that fires when the server refuses an action for
// entitlement (modals/paywall.js), and Profile's own billing button.
//
// Its own module, importing only from shared/, so it can be tested without a
// browser. paywall.js reads `window` at load through lib/api.js, which is
// fine in a browser and means nothing in that file can be unit-tested.

import { subscriptionSituation } from "../../../shared/company-export.js";

/**
 * Whether this subscription status needs the billing PORTAL rather than
 * Checkout. The question is whether a subscription is still RUNNING: Checkout
 * starts one, the portal manages or repairs one, and asking Checkout to start
 * a second earns a 409 AlreadySubscribed.
 *
 * One that has ended (`canceled`, `incomplete_expired`) goes to Checkout
 * (30 October 2026): the portal can't start a subscription, so "Subscribe
 * again" landed on a page with nothing to subscribe to, and for a company
 * whose App Store subscription had ended the portal is refused outright
 * ("managed in the App Store"), so it could never subscribe on the web at
 * all. So does one that never paid (`incomplete`, `paused`): a completed
 * Checkout takes over from it and cancels it (takeOverOrRefuse in
 * server/routes/billing.js).
 *
 * Profile used to call Checkout whatever the status was, so somebody past due
 * pressed Subscribe and got that 409 — on the one screen where they were
 * trying to give us money.
 *
 * `active` belongs here for Profile's sake. The 402 path can't see it (an
 * active subscriber is never refused for entitlement), but Profile's "Manage
 * billing" is exactly that case, and a rule with a hole in it is worse than
 * no rule at all.
 *
 * `trialing` is a subscription too, and this is the one that reads backwards.
 * It is a STRIPE status, so it means a real subscription is in its trial — it
 * has a card on it and the portal can manage it. Sendinvo's own 30-day no-card
 * trial is a different thing entirely: that reports `status: "none"` with a
 * separate `inTrial` flag, and correctly goes to Checkout.
 *
 * Anything unrecognised goes to Checkout, which is the safe default: it
 * refuses an account that already has a subscription, where the portal would
 * open on nothing.
 *
 * @param {string} status  subscription.status as the server reports it
 * @returns {boolean}
 */
export function subscriptionNeedsPortal(status) {
  return status === "active" || status === "trialing"
    || status === "past_due" || status === "unpaid";
}

/**
 * What to tell somebody who cannot act on the company's billing.
 *
 * They pressed Send on an invoice. They are not the person who pays, they may
 * not have known there was a subscription at all, and there is nothing for
 * them to do here — so the sentence says what is wrong and who can fix it,
 * and stops. It does not say "you don't have permission", which is what the
 * billing route's 403 used to surface: true, unhelpful, and about the wrong
 * thing.
 *
 * @param {string} status  subscription.status as the server reports it
 * @returns {string}
 */
export function subscriptionLapsedForOthers(status) {
  const situation = subscriptionSituation(status);
  if (situation === "payment") {
    return "This company\u2019s subscription needs attention before anything else can be sent. An owner or admin can sort it out.";
  }
  if (situation === "ended") {
    return "This company\u2019s subscription has ended. An owner or admin can renew it.";
  }
  return "This company needs a subscription before anything else can be sent. An owner or admin can start one.";
}
